import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

/**
 * Check all users for missing OneSignal IDs and set up automatic assignment
 * Note: OneSignal IDs are device-specific and can only be generated on each user's device
 */
export const updateMissingOneSignalIds = async (): Promise<{
  success: boolean;
  updated: number;
  errors: number;
  message: string;
  usersWithoutIds: Array<{id: string, email?: string, name?: string}>;
  currentUserUpdated: boolean;
}> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return {
        success: false,
        updated: 0,
        errors: 0,
        message: 'Not running on native platform - OneSignal only works on mobile devices',
        usersWithoutIds: [],
        currentUserUpdated: false
      };
    }

    // Get all users without OneSignal IDs
    const { data: usersWithoutIds, error: usersError } = await supabase
      .from('users')
      .select('id, email, name')
      .is('onesignal_user_id', null);

    if (usersError) {
      
      return {
        success: false,
        updated: 0,
        errors: 1,
        message: 'Error fetching users without OneSignal IDs',
        usersWithoutIds: [],
        currentUserUpdated: false
      };
    }

    const usersWithoutIdsArray = usersWithoutIds || [];

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        success: true,
        updated: 0,
        errors: 0,
        message: `Found ${usersWithoutIdsArray.length} users without OneSignal IDs. Auto-assignment will happen when they open the app.`,
        usersWithoutIds: usersWithoutIdsArray,
        currentUserUpdated: false
      };
    }

    // Check if current user is among those missing OneSignal ID
    const currentUserNeedsId = usersWithoutIdsArray.some(u => u.id === user.id);
    let currentUserUpdated = false;

    if (currentUserNeedsId) {
      // Try to get OneSignal ID for current user (the only one we can actually update)
      try {
        const { initializeOneSignal } = await import('@/integrations/onesignal');
        
        // Initialize OneSignal if not already initialized
        await initializeOneSignal();
        
        // Get the adapter and device state
        const oneSignal = await import('@/integrations/onesignal');
        const adapter = (oneSignal as any).getOneSignalAdapter();
        
        if (adapter && adapter.isAvailable()) {
          const deviceState = await adapter.getDeviceState();
          
          if (deviceState?.userId) {
            // Update current user with OneSignal ID
            const { error: updateError } = await supabase
              .from('users')
              .update({ onesignal_user_id: deviceState.userId })
              .eq('id', user.id);

            if (!updateError) {
              currentUserUpdated = true;
              // Remove current user from the list since we updated them
              const updatedList = usersWithoutIdsArray.filter(u => u.id !== user.id);
              
              return {
                success: true,
                updated: 1,
                errors: 0,
                message: `Updated current user's OneSignal ID. ${updatedList.length} other users still need OneSignal IDs (will be assigned when they open the app).`,
                usersWithoutIds: updatedList,
                currentUserUpdated: true
              };
            }
          }
        }
      } catch (onesignalError) {
        
      }
    }

    // Set up automatic OneSignal ID assignment flag in database
    // This ensures that when users without OneSignal IDs next log in, they'll get their IDs assigned
    try {
      // We could add a flag to trigger more aggressive OneSignal ID assignment
      // For now, the existing login logic will handle this automatically
      
      const message = currentUserNeedsId && !currentUserUpdated 
        ? `Found ${usersWithoutIdsArray.length} users without OneSignal IDs (including current user). Auto-assignment enabled for when they open the app.`
        : `Found ${usersWithoutIdsArray.length} users without OneSignal IDs. Auto-assignment enabled for when they open the app.`;

      return {
        success: true,
        updated: currentUserUpdated ? 1 : 0,
        errors: 0,
        message,
        usersWithoutIds: usersWithoutIdsArray,
        currentUserUpdated
      };
    } catch (error) {
      
      return {
        success: false,
        updated: currentUserUpdated ? 1 : 0,
        errors: 1,
        message: 'Partial success - some operations failed',
        usersWithoutIds: usersWithoutIdsArray,
        currentUserUpdated
      };
    }

  } catch (error) {
    
    return {
      success: false,
      updated: 0,
      errors: 1,
      message: 'Migration failed',
      usersWithoutIds: [],
      currentUserUpdated: false
    };
  }
};

/**
 * Check if current user has OneSignal ID and update if missing
 * This is a lighter version that can be called on each login
 */
export const ensureCurrentUserHasOneSignalId = async (): Promise<boolean> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return false;
    }

    // Check if user has OneSignal ID
    const { data: userData } = await supabase
      .from('users')
      .select('onesignal_user_id')
      .eq('id', user.id)
      .single();

    if (userData?.onesignal_user_id) {
      return true; // Already has ID
    }

    // Try to get and save OneSignal ID
    const { initializeOneSignalForUser } = await import('@/integrations/onesignal');
    await initializeOneSignalForUser();
    
    return true;
  } catch (error) {
    
    return false;
  }
};

/**
 * Admin function to get statistics about OneSignal ID coverage
 */
export const getOneSignalIdStats = async (): Promise<{
  total: number;
  withIds: number;
  withoutIds: number;
  coverage: number;
}> => {
  try {
    // Get total users count
    const { count: totalCount, error: totalError } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true });

    if (totalError) {
      throw totalError;
    }

    // Get users with OneSignal IDs count
    const { count: withIdsCount, error: withIdsError } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .not('onesignal_user_id', 'is', null);

    if (withIdsError) {
      throw withIdsError;
    }

    const total = totalCount || 0;
    const withIds = withIdsCount || 0;
    const withoutIds = total - withIds;
    const coverage = total > 0 ? (withIds / total) * 100 : 0;

    return {
      total,
      withIds,
      withoutIds,
      coverage: Math.round(coverage * 100) / 100
    };
  } catch (error) {
    
    return {
      total: 0,
      withIds: 0,
      withoutIds: 0,
      coverage: 0
    };
  }
};

/**
 * Send push notifications to users with OneSignal IDs to encourage app usage
 * This can help users without OneSignal IDs to open the app and get their IDs assigned
 */
export const sendEncouragementNotifications = async (): Promise<{
  success: boolean;
  message: string;
  sentCount: number;
}> => {
  try {
    // Get users who have OneSignal IDs (we can send notifications to them)
    const { data: usersWithIds, error } = await supabase
      .from('users')
      .select('id, onesignal_user_id, email, name')
      .not('onesignal_user_id', 'is', null);

    if (error) {
      throw error;
    }

    if (!usersWithIds || usersWithIds.length === 0) {
      return {
        success: false,
        message: 'Bildirim gönderilecek OneSignal ID\'si olan kullanıcı bulunamadı',
        sentCount: 0
      };
    }

    // Prepare OneSignal IDs for notification
    const oneSignalIds = usersWithIds.map(user => user.onesignal_user_id).filter(Boolean);

    if (oneSignalIds.length === 0) {
      return {
        success: false,
        message: 'Geçerli OneSignal ID bulunamadı',
        sentCount: 0
      };
    }

    // Import OneSignal notification function
    const { sendNotification } = await import('@/integrations/onesignal');

    // Send notification to encourage app usage
    const result = await sendNotification(
      oneSignalIds,
      'ŞantiyeX Güncellemesi',
      'Uygulamada yeni özellikler var! Şimdi açın ve keşfedin.',
      {
        type: 'app_update_encouragement',
        action: 'open_app'
      }
    );

    if (result.success) {
      return {
        success: true,
        message: `${oneSignalIds.length} kullanıcıya teşvik bildirimi gönderildi`,
        sentCount: oneSignalIds.length
      };
    } else {
      return {
        success: false,
        message: result.message || 'Bildirim gönderimi başarısız',
        sentCount: 0
      };
    }

  } catch (error) {
    
    return {
      success: false,
      message: 'Bildirim gönderimi sırasında hata oluştu',
      sentCount: 0
    };
  }
};

/**
 * Get detailed information about users and their OneSignal status
 */
export const getUserOneSignalDetails = async (): Promise<{
  success: boolean;
  users: Array<{
    id: string;
    email?: string;
    name?: string;
    hasOneSignalId: boolean;
    lastLogin?: string;
    createdAt?: string;
  }>;
}> => {
  try {
    const { data: users, error } = await supabase
      .from('users')
      .select('id, email, name, onesignal_user_id, created_at')
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    const userDetails = (users || []).map(user => ({
      id: user.id,
      email: user.email,
      name: user.name,
      hasOneSignalId: !!user.onesignal_user_id,
      createdAt: user.created_at
    }));

    return {
      success: true,
      users: userDetails
    };
  } catch (error) {
    
    return {
      success: false,
      users: []
    };
  }
}; 